/**
 * DV-WS-001: WebSocket Demo - Visual Debugger
 *
 * Progressive visual testing of WebSocket components:
 * 1. Execution Selector - API integration test
 * 2. Token Acquisition - Auth flow test
 * 3. Connection Lifecycle - WebSocket connection test
 * 4. Full Integration - Complete flow test
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TestBlock } from './TestBlock';
import { EventFeed, type WebSocketEvent } from './EventFeed';
import { type LogLevel } from './LogsPanel';
import { useSecureWebSocketV2 } from '@/hooks/useSecureWebSocket-v2';
import { ConnectionStatusIndicator } from '@/components/ui/ConnectionStatus';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { buildApiUrl, getApiHeadersGet } from '@/lib/api-base';
import { timelineKeys } from '@/types/execution-events';
import { useStatusReport } from './useStatusReport';
import { useDemoLogger, type DemoLogEntry } from '@/lib/demo-logger';
import { Play, Square, RotateCcw, RefreshCw, Clock, Wifi, AlertTriangle, Shield, Zap, WifiOff, Copy, Check } from 'lucide-react';

// Execution data type
interface ExecutionItem {
  id: string;
  title: string;
  status: string;
  created_at: string;
}

interface TokenInfo {
  hasToken: boolean;
  expiresAt?: number;
  timeToExpiry?: number;
  tokenLength?: number;
}

interface WebSocketDemoProps {
  onLog?: (entry: DemoLogEntry) => void;
}

export const WebSocketDemo: React.FC<WebSocketDemoProps> = ({ onLog }) => {
  // Demo state - initialize with fallback execution ID
  const [selectedExecutionId, setSelectedExecutionId] = useState<string>('550e8400-e29b-41d4-a716-446655440000');
  const [executions, setExecutions] = useState<ExecutionItem[]>([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo>({ hasToken: false });
  const [events, setEvents] = useState<WebSocketEvent[]>([]);

  // Initialize DemoLogger for WebSocket Demo
  const demoLogger = useDemoLogger('websocket', {
    component: 'WebSocketDemo',
    onLog: (entry: DemoLogEntry) => {
      setRecentLogs(prev => {
        const updated = [...prev, entry];
        return updated.length > 200 ? updated.slice(-200) : updated;
      });

      // Track API calls separately
      if (entry.meta.action && ['fetch_start', 'fetch_success', 'fetch_error'].includes(entry.meta.action)) {
        setRecentApiCalls(prev => {
          const updated = [...prev, entry];
          return updated.length > 50 ? updated.slice(-50) : updated;
        });
      }

      // Forward to global log handler using DemoLogEntry directly
      if (onLog) {
        onLog(entry);
      }
    }
  });

  // Failure simulation state
  const [enablePollingFallback, setEnablePollingFallback] = useState(true);
  const [testExecutionId, setTestExecutionId] = useState<string>('');
  const [simulationInProgress, setSimulationInProgress] = useState<string | null>(null);

  // Auth and Query Client
  const { isAuthenticated } = useServerAuth();
  const queryClient = useQueryClient();

  // Status Report functionality (DV-UX-002)
  const { buildWebSocketReport } = useStatusReport();
  const [recentLogs, setRecentLogs] = useState<DemoLogEntry[]>([]);
  const [recentApiCalls, setRecentApiCalls] = useState<any[]>([]);
  const [copySuccess, setCopySuccess] = useState(false);

  // Enhanced logging utility using DemoLogger directly
  const addLog = useCallback((source: string, message: string, level: LogLevel = 'info', extraMeta?: any) => {
    demoLogger.log(level, `[${source.toUpperCase()}] ${message}`, undefined, {
      source,
      correlationId: `ws-demo-${Date.now()}`,
      ...extraMeta
    });
  }, [demoLogger]);


  // Memoized WebSocket callbacks to prevent infinite re-renders
  const onConnectionChange = useCallback((status: string) => {
    demoLogger.info(`WebSocket status changed to: ${status}`, 'connection_open', {
      connectionStatus: status, // Use different field name to avoid type conflict
      pollingFallback: enablePollingFallback,
      executionId: selectedExecutionId
    });

    if (status === 'connected') {
      addLog('websocket', 'Listening for pipeline_event, execution_update, stage_update events', 'info');
    } else if (status === 'fallback') {
      addLog('websocket', '📊 Polling fallback activated due to WebSocket failure', 'warn');
    }
  }, [demoLogger, addLog, enablePollingFallback, selectedExecutionId]);

  const onAuthFailure = useCallback((error: any) => {
    demoLogger.error('WebSocket authentication failed', 'auth_check', {
      error,
      pollingFallback: enablePollingFallback,
      recovery: enablePollingFallback ? 'polling_fallback_available' : 'reconnect_only'
    });

    if (enablePollingFallback) {
      addLog('websocket', '📊 Will attempt polling fallback after auth failure', 'info');
    }
  }, [demoLogger, addLog, enablePollingFallback]);

  const onEvent = useCallback((eventType: string, payload: any) => {
    // Create WebSocket event for display
    const webSocketEvent: WebSocketEvent = {
      event_type: eventType,
      stage: payload.stage,
      execution_id: payload.execution_id || selectedExecutionId,
      timestamp: payload.timestamp || new Date().toISOString(),
      payload: payload,
      correlation_id: payload.correlation_id,
      workflow_run_id: payload.workflow_run_id,
      attempt_number: payload.attempt_number,
    };

    // Add to events feed
    setEvents(prev => [...prev.slice(-99), webSocketEvent]);

    addLog('websocket', `🔴 Live event: ${eventType}${payload.stage ? ` (${payload.stage})` : ''}`, 'info');

    // Cache invalidation for real-time updates
    if (selectedExecutionId) {
      if (eventType === 'pipeline_event') {
        queryClient.invalidateQueries({
          queryKey: timelineKeys.execution(selectedExecutionId)
        });
      }
      if (eventType === 'execution_update') {
        queryClient.invalidateQueries({
          queryKey: ['execution', selectedExecutionId]
        });
      }
    }
  }, [selectedExecutionId, addLog, queryClient]);

  const onReconnectScheduled = useCallback((delayMs: number, attempt: number) => {
    demoLogger.info('Reconnection attempt scheduled', undefined, {
      delayMs: Math.round(delayMs),
      attempt,
      backoffPattern: 'exponential_with_jitter'
    });
  }, [demoLogger]);


  // Initialize WebSocket hook with memoized callbacks
  const webSocketState = useSecureWebSocketV2({
    executionId: selectedExecutionId,
    onConnectionChange,
    onAuthFailure,
    onEvent,
    onReconnectScheduled,
    enablePollingFallback
  });

  // DV-UX-002: Copy status report to clipboard
  const copyStatusReport = useCallback(async () => {
    try {
      // Debug: Log what we're sending to the status report
      console.log(`[DEBUG-STATUS-REPORT] Generating report with ${recentLogs.length} logs`);
      console.log('[DEBUG-STATUS-REPORT] Recent logs sample:', recentLogs.slice(-5).map(log => log.text.slice(0, 100)));

      // Add execution selector debug data to status report
      const executionSelectorData = {
        selectedExecutionId: selectedExecutionId || null,
        availableExecutions: executions,
        lastFetchStatus: loadingExecutions ? 'loading' as const :
                        executions.length > 0 ? 'success' as const : 'error' as const,
        lastFetchError: executions.length === 0 && !loadingExecutions ? 'No executions found' : undefined,
        lastUpdatedAt: executions.length > 0 ? new Date().toISOString() : undefined
      };

      const report = buildWebSocketReport(
        webSocketState,
        recentLogs,
        recentApiCalls,
        events,
        executionSelectorData
      );

      await navigator.clipboard.writeText(report);
      setCopySuccess(true);

      // Log the copy event
      addLog('status-report', 'Status report copied to clipboard', 'info', {
        correlationId: `status-copy-${Date.now()}`,
        reportSize: report.length,
        eventsCount: events.length,
        logsCount: recentLogs.length,
        apiCallsCount: recentApiCalls.length
      });

      // Reset success indicator after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      addLog('status-report', `Failed to copy status report: ${error}`, 'error');
    }
  }, [buildWebSocketReport, webSocketState, recentLogs, recentApiCalls, events, addLog]);

  // Track API calls for status report
  const trackApiCall = useCallback((method: string, path: string, status: number, elapsed: number, requestId?: string) => {
    const apiCall = {
      method,
      path,
      status,
      elapsed,
      requestId,
      timestamp: new Date().toISOString()
    };

    setRecentApiCalls(prev => [...prev, apiCall].slice(-20)); // Keep last 20
  }, []);

  // 1. EXECUTION SELECTOR TEST
  const testExecutionSelector = async () => {
    const endpoint = '/api/demo/executions?limit=20'; // Use demo/admin endpoint
    const fullUrl = buildApiUrl(endpoint);
    addLog('api', 'Testing execution selector API (demo/admin scope)...', 'info');
    addLog('api', `🌐 Calling: ${fullUrl}`, 'info');
    setLoadingExecutions(true);

    const startTime = Date.now();
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        credentials: 'include',
        headers: getApiHeadersGet(), // Preflight-safe headers for GET
      });

      const elapsed = Date.now() - startTime;
      const requestId = response.headers.get('X-Request-Id') || undefined;

      trackApiCall('GET', '/api/demo/executions', response.status, elapsed, requestId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // CRITICAL DEBUG: Log the exact API response
      addLog('api', `🔍 FULL API RESPONSE: ${JSON.stringify(data).slice(0, 500)}`, 'info');
      addLog('api', `🔍 Response keys: ${JSON.stringify(Object.keys(data))}`, 'info');
      addLog('api', `🔍 Response type: ${typeof data}`, 'info');

      // Robust parsing for all common API response shapes
      let executionList: ExecutionItem[] = [];

      if (Array.isArray(data)) {
        // Direct array response
        executionList = data;
      } else if (data.executions && Array.isArray(data.executions)) {
        // { executions: [...] }
        executionList = data.executions;
      } else if (data.items && Array.isArray(data.items)) {
        // { items: [...] }
        executionList = data.items;
      } else if (data.data) {
        // Nested data structure
        if (Array.isArray(data.data)) {
          // { data: [...] }
          executionList = data.data;
        } else if (data.data.executions && Array.isArray(data.data.executions)) {
          // { data: { executions: [...] } }
          executionList = data.data.executions;
        } else if (data.data.items && Array.isArray(data.data.items)) {
          // { data: { items: [...] } }
          executionList = data.data.items;
        }
      } else if (data.results && Array.isArray(data.results)) {
        // { results: [...] }
        executionList = data.results;
      }

      // Limit to 10 for demo
      executionList = executionList.slice(0, 10);

      // Enhanced debugging for execution selection
      addLog('api', `📊 Raw API response structure: ${JSON.stringify(Object.keys(data))}`, 'info');
      addLog('api', `📊 Parsed execution count: ${executionList.length}`, 'info');
      addLog('api', `📊 Current selectedExecutionId: "${selectedExecutionId}" (empty: ${!selectedExecutionId})`, 'info');

      if (executionList.length > 0) {
        addLog('api', `📊 First execution: ${JSON.stringify(executionList[0])}`, 'info');
      }

      setExecutions(executionList);
      addLog('api', `✅ Loaded ${executionList.length} executions`, 'info');

      if (executionList.length > 0) {
        const firstExecution = executionList[0];
        const executionId = firstExecution.id;

        // Always prefer real executions over fallback
        if (selectedExecutionId === '550e8400-e29b-41d4-a716-446655440000' || !selectedExecutionId) {
          setSelectedExecutionId(executionId);
          addLog('api', `🎯 Auto-selected real execution: ${executionId}`, 'info');
          addLog('api', `🎯 Selected execution details: ${JSON.stringify(firstExecution)}`, 'info');
        } else {
          addLog('api', `🎯 Keeping current selection: ${selectedExecutionId}`, 'debug');
        }
      } else {
        addLog('api', `⚠️ No executions available, keeping fallback: ${selectedExecutionId}`, 'warn');
        addLog('api', `⚠️ Checked all common response fields - found no execution arrays`, 'warn');
        addLog('api', `⚠️ Available fields in response: ${Object.keys(data).join(', ')}`, 'warn');
        addLog('api', `ℹ️ Using admin demo endpoint - if still empty, server may have no test data`, 'info');
      }
    } catch (error) {
      const elapsed = Date.now() - startTime;
      trackApiCall('GET', '/api/executions', 0, elapsed); // 0 for error
      addLog('api', `❌ Failed to load executions: ${error}`, 'error');
      setExecutions([]);
    } finally {
      setLoadingExecutions(false);
    }
  };

  // 2. TOKEN ACQUISITION TEST
  const testTokenAcquisition = async () => {
    addLog('auth', 'Testing WebSocket token acquisition...', 'info');

    const startTime = Date.now();
    try {
      const response = await fetch(buildApiUrl('/api/auth/ws-token'), {
        method: 'GET',
        credentials: 'include',
        headers: getApiHeadersGet(), // Preflight-safe headers for GET
      });

      const elapsed = Date.now() - startTime;
      const requestId = response.headers.get('X-Request-Id') || undefined;

      trackApiCall('GET', '/api/auth/ws-token', response.status, elapsed, requestId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.token && data.expiresAt) {
        const expiresAt = new Date(data.expiresAt).getTime();
        const timeToExpiry = Math.max(0, expiresAt - Date.now());

        setTokenInfo({
          hasToken: true,
          expiresAt,
          timeToExpiry,
          tokenLength: data.token.length
        });

        addLog('auth', `✅ Token acquired: ${data.token.length} chars, expires in ${Math.round(timeToExpiry / 1000)}s`, 'info');
      } else {
        throw new Error('Invalid token response format');
      }
    } catch (error) {
      addLog('auth', `❌ Token acquisition failed: ${error}`, 'error');
      setTokenInfo({ hasToken: false });
    }
  };

  // 3. CONNECTION LIFECYCLE TEST
  const testConnectionLifecycle = () => {
    addLog('websocket', `🔍 Connection test debug: selectedExecutionId="${selectedExecutionId}" (length: ${selectedExecutionId.length})`, 'debug');
    addLog('websocket', `🔍 Executions available: ${executions.length}`, 'debug');
    addLog('websocket', `🔍 WebSocket status: ${webSocketState.status}`, 'debug');

    if (!selectedExecutionId) {
      addLog('websocket', '❌ No execution selected for connection test', 'warn');
      addLog('websocket', `❌ Available executions: ${executions.map(e => e.id).join(', ')}`, 'warn');
      return;
    }

    addLog('websocket', `🚀 Testing connection lifecycle for execution: ${selectedExecutionId}`, 'info');
    addLog('websocket', '🏠 Production hook will join user room and listen for execution-specific events', 'info');
    addLog('websocket', `🎯 WebSocket will auto-connect since executionId is now set`, 'info');
  };

  const disconnectWebSocket = () => {
    addLog('websocket', 'Manually disconnecting WebSocket...', 'info');
    webSocketState.disconnect();
  };

  const reconnectWebSocket = () => {
    addLog('websocket', 'Manually reconnecting WebSocket...', 'info');
    webSocketState.reconnect();
  };

  // DEMO EVENT SIMULATION (for testing without real events)
  const simulateWebSocketEvent = useCallback((eventType: string) => {
    if (!selectedExecutionId) {
      addLog('demo', '❌ No execution selected for event simulation', 'warn');
      return;
    }

    const mockEventData = {
      execution_id: selectedExecutionId,
      stage: 'script',
      timestamp: new Date().toISOString(),
      correlation_id: `demo-${Date.now()}`,
      workflow_run_id: `demo-workflow-${Date.now()}`,
      attempt_number: 1,
      payload: {
        status: 'completed',
        iteration: 1,
        score: 0.95,
        demo_simulation: true
      }
    };

    addLog('demo', `Simulating ${eventType} event for testing`, 'info');

    // Simulate event using the same logic as onEvent callback
    const webSocketEvent: WebSocketEvent = {
      event_type: eventType,
      stage: mockEventData.stage,
      execution_id: mockEventData.execution_id,
      timestamp: mockEventData.timestamp,
      payload: mockEventData,
      correlation_id: mockEventData.correlation_id,
      workflow_run_id: mockEventData.workflow_run_id,
      attempt_number: mockEventData.attempt_number,
    };

    setEvents(prev => [...prev.slice(-99), webSocketEvent]);
  }, [selectedExecutionId, addLog]);

  // FAILURE SIMULATION FUNCTIONS
  const simulateInvalidToken = useCallback(async () => {
    setSimulationInProgress('invalid_token');
    demoLogger.warn('Token override simulation disabled in production mode', 'test', {
      simulation: 'invalid_token',
      note: 'This simulation requires local development setup'
    });
    setTimeout(() => setSimulationInProgress(null), 3000);
  }, []);

  const simulateTokenExpiry = useCallback(async () => {
    setSimulationInProgress('token_expiry');
    demoLogger.warn('Token expiry simulation disabled in production mode', 'test', {
      simulation: 'token_expiry',
      note: 'This simulation requires local development setup'
    });
    setTimeout(() => setSimulationInProgress(null), 3000);
  }, []);

  const simulateServerDisconnect = useCallback(() => {
    setSimulationInProgress('server_disconnect');
    demoLogger.info('Starting server disconnect simulation', 'test', {
      simulation: 'server_disconnect',
      expectedOutcome: 'disconnect_event',
      recoveryPath: enablePollingFallback ? 'exponential_backoff_then_polling' : 'exponential_backoff'
    });

    try {
      // Use the existing disconnect method
      webSocketState.disconnect();

      demoLogger.warn('Socket disconnect initiated', 'test', {
        simulation: 'server_disconnect_triggered',
        expectedEvents: ['disconnect_reason', 'reconnect_scheduled_in_ms', 'reconnected', 'fallback_started'],
        backoffPattern: 'exponential_with_jitter',
        maxReconnectAttempts: 'unlimited',
        fallbackEnabled: enablePollingFallback
      });
    } catch (error) {
      demoLogger.error('Simulation setup error', 'test', {
        simulation: 'server_disconnect_simulation_failed',
        error: String(error)
      });
    } finally {
      setTimeout(() => setSimulationInProgress(null), 10000);
    }
  }, [webSocketState, enablePollingFallback]);

  const forceReauth = useCallback(async () => {
    if (!webSocketState.forceReauth) {
      demoLogger.error('Reauth method not available', 'test', {
        simulation: 'force_reauth',
        available: false
      });
      return;
    }

    setSimulationInProgress('force_reauth');
    demoLogger.info('Manual reauth process initiated', 'test', {
      simulation: 'force_reauth_start',
      operation: 'reauth',
      expectedOutcome: 'new_valid_token'
    });

    try {
      const result = await webSocketState.forceReauth();
      demoLogger.info('Token refresh completed successfully', 'test', {
        simulation: 'force_reauth_success',
        tokenLength: result?.token?.length || 'unknown',
        expiresIn: result?.expiresAt ? Math.round((result.expiresAt - Date.now()) / 1000) : 'unknown'
      });
    } catch (error) {
      demoLogger.error('Token refresh failed', 'test', {
        simulation: 'force_reauth_failed',
        error: String(error),
        recoveryPath: 'manual_intervention_required'
      });
    } finally {
      setSimulationInProgress(null);
    }
  }, [webSocketState]);

  // RLS DENY/404 SIMULATION
  const simulateRlsDeny = useCallback(async () => {
    const targetExecutionId = testExecutionId || 'non-existent-execution-12345';
    setSimulationInProgress('rls_deny');

    demoLogger.info('Testing execution access permissions', 'test', {
      simulation: 'rls_test_start',
      targetExecutionId,
      testType: testExecutionId ? 'user_provided_id' : 'synthetic_invalid_id',
      endpoint: `/api/executions/${targetExecutionId}`
    });

    try {
      const response = await fetch(buildApiUrl(`/api/executions/${targetExecutionId}`), {
        method: 'GET',
        credentials: 'include',
        headers: getApiHeadersGet(),
      });

      if (!response.ok) {
        const statusText = response.statusText || 'Unknown error';
        demoLogger.warn(`HTTP ${response.status}: ${statusText}`, 'test', {
          simulation: 'rls_test_denied',
          statusCode: response.status,
          statusText,
          targetExecutionId,
          testResult: 'access_denied_as_expected'
        });

        if (response.status === 403) {
          demoLogger.info('RLS policy denied access', 'test', {
            simulation: 'rls_guidance_403',
            guidance: 'This execution exists but you lack access permissions (RLS)',
            rootCause: 'row_level_security_policy',
            solution: 'Check execution ownership or team membership'
          });
        } else if (response.status === 404) {
          demoLogger.info('Execution not found or not accessible', 'test', {
            simulation: 'rls_guidance_404',
            guidance: 'This execution does not exist or is not accessible',
            rootCause: 'nonexistent_resource_or_rls_policy',
            solution: 'Verify execution ID and access permissions'
          });
        }
      } else {
        demoLogger.info('Unexpected access granted', 'test', {
          simulation: 'rls_test_success',
          statusCode: response.status,
          targetExecutionId,
          testResult: 'access_granted_unexpectedly',
          guidance: 'Execution was accessible - check if this was intended'
        });
      }
    } catch (error) {
      demoLogger.error('Network error during RLS test', 'test', {
        simulation: 'rls_test_error',
        error: String(error),
        targetExecutionId,
        testResult: 'network_failure'
      });
    } finally {
      setTimeout(() => setSimulationInProgress(null), 3000);
    }
  }, [testExecutionId]);

  // 4. FULL INTEGRATION TEST
  const testFullIntegration = async () => {
    addLog('integration', '🚀 Starting full integration test...', 'info');

    // Step 1: Load executions
    await testExecutionSelector();

    // Step 2: Test token
    setTimeout(async () => {
      await testTokenAcquisition();

      // Step 3: Connect WebSocket
      setTimeout(() => {
        testConnectionLifecycle();
        addLog('integration', '✅ Full integration test initiated', 'info');
      }, 1000);
    }, 1000);
  };

  // Auto-load executions on mount
  useEffect(() => {
    addLog('init', `🔄 Auth status changed: isAuthenticated=${isAuthenticated}`, 'info');
    if (isAuthenticated) {
      addLog('init', '🚀 Auto-loading executions on mount', 'info');
      addLog('init', '🧪 TEST: This log should appear in status report', 'info');
      testExecutionSelector();
    } else {
      addLog('init', '⏳ Waiting for authentication before loading executions', 'info');
    }
  }, [isAuthenticated]);

  // Debug selectedExecutionId changes
  useEffect(() => {
    addLog('debug', `🎯 selectedExecutionId changed: "${selectedExecutionId}" (length: ${selectedExecutionId.length})`, 'info');
    addLog('debug', `🎯 Available executions: ${executions.length}`, 'info');
  }, [selectedExecutionId, executions.length]);

  // Token TTL countdown
  useEffect(() => {
    if (!tokenInfo.hasToken || !tokenInfo.expiresAt) return;

    const interval = setInterval(() => {
      const timeToExpiry = Math.max(0, tokenInfo.expiresAt! - Date.now());
      setTokenInfo(prev => ({ ...prev, timeToExpiry }));

      if (timeToExpiry <= 0) {
        addLog('auth', '⚠️ Token expired', 'warn');
        setTokenInfo({ hasToken: false });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [tokenInfo.expiresAt, addLog]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--fg-primary)' }}>
            WebSocket Demo - Visual Debugger
          </h2>
          <p className="text-sm" style={{ color: 'var(--fg-secondary)' }}>
            Progressive testing: API → Auth → Connection → Events → Failures → Integration
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={copyStatusReport}
            className="flex items-center gap-2 px-3 py-1 text-sm rounded transition-colors"
            style={{
              backgroundColor: copySuccess ? 'var(--accent)' : 'var(--bg-interactive-hover)',
              color: copySuccess ? 'var(--bg-surface)' : 'var(--fg-primary)',
              border: '1px solid var(--border-default)'
            }}
            data-testid="dv-status-copy"
            title="Copy LLM-ready status report to clipboard"
          >
            {copySuccess ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copySuccess ? 'Copied!' : 'Copy Status'}
          </button>
          <button
            onClick={testFullIntegration}
            className="flex items-center gap-2 px-3 py-1 text-sm rounded"
            style={{
              backgroundColor: 'var(--accent)',
              color: 'var(--bg-surface)'
            }}
          >
            <Play className="w-4 h-4" />
            Run All Tests
          </button>
        </div>
      </div>

      {/* Test Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* 1. EXECUTION SELECTOR TEST */}
        <TestBlock
          title="1. Execution Selector"
          description="Test API integration and execution loading"
          status={executions.length > 0 ? 'success' : loadingExecutions ? 'info' : 'idle'}
          actions={
            <button
              onClick={testExecutionSelector}
              disabled={loadingExecutions}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded"
              style={{
                backgroundColor: 'var(--bg-interactive-hover)',
                color: 'var(--fg-primary)'
              }}
            >
              <RefreshCw className={`w-3 h-3 ${loadingExecutions ? 'animate-spin' : ''}`} />
              {loadingExecutions ? 'Loading...' : 'Test API'}
            </button>
          }
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: 'var(--fg-secondary)' }}>API Endpoint:</span>
              <code className="text-xs" style={{ color: 'var(--fg-primary)' }}>
                {buildApiUrl('/api/demo/executions')}
              </code>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span style={{ color: 'var(--fg-secondary)' }}>Loaded Executions:</span>
              <span style={{ color: 'var(--fg-primary)' }}>{executions.length}</span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span style={{ color: 'var(--fg-secondary)' }}>Selected Execution:</span>
              <code className="text-xs" style={{
                color: selectedExecutionId ? 'var(--accent)' : 'var(--fg-muted)',
                backgroundColor: 'var(--bg-elevated)',
                padding: '2px 4px',
                borderRadius: '2px'
              }}>
                {selectedExecutionId || 'None'}
              </code>
            </div>

            {/* Fallback execution selection from recent events */}
            {executions.length === 0 && events.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs" style={{ color: 'var(--fg-secondary)' }}>
                  Use execution from recent events:
                </label>
                <button
                  onClick={() => {
                    const recentEvent = events[0];
                    if (recentEvent.execution_id) {
                      setSelectedExecutionId(recentEvent.execution_id);
                      addLog('api', `🎯 Using execution from recent event: ${recentEvent.execution_id}`, 'info');
                    }
                  }}
                  className="w-full px-2 py-1 text-xs rounded border transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-surface)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--fg-primary)'
                  }}
                  disabled={!events[0]?.execution_id}
                >
                  Use: {events[0]?.execution_id?.slice(-12) || 'No execution in events'}
                </button>
              </div>
            )}

            {executions.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs" style={{ color: 'var(--fg-secondary)' }}>
                  Select Execution:
                </label>
                <select
                  value={selectedExecutionId}
                  onChange={(e) => {
                    const newExecutionId = e.target.value;
                    addLog('api', `🎯 Manual execution selection: "${newExecutionId}"`, 'info');
                    addLog('api', `🎯 Previous execution: "${selectedExecutionId}"`, 'debug');
                    setSelectedExecutionId(newExecutionId);
                    addLog('api', `🎯 Execution selection updated`, 'debug');
                  }}
                  className="w-full p-2 text-sm rounded"
                  style={{
                    backgroundColor: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--fg-primary)'
                  }}
                >
                  {executions.map((exec) => (
                    <option key={exec.id} value={exec.id}>
                      {exec.title || exec.id} ({exec.status})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </TestBlock>

        {/* 2. TOKEN ACQUISITION TEST */}
        <TestBlock
          title="2. Token Acquisition"
          description="Test WebSocket token fetching and TTL"
          status={tokenInfo.hasToken ? 'success' : 'idle'}
          actions={
            <button
              onClick={testTokenAcquisition}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded"
              style={{
                backgroundColor: 'var(--bg-interactive-hover)',
                color: 'var(--fg-primary)'
              }}
            >
              <RefreshCw className="w-3 h-3" />
              Get Token
            </button>
          }
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: 'var(--fg-secondary)' }}>Token Status:</span>
              <span style={{ color: tokenInfo.hasToken ? 'var(--accent)' : 'var(--fg-secondary)' }}>
                {tokenInfo.hasToken ? '✅ Active' : '❌ None'}
              </span>
            </div>

            {tokenInfo.hasToken && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: 'var(--fg-secondary)' }}>Token Length:</span>
                  <span style={{ color: 'var(--fg-primary)' }}>{tokenInfo.tokenLength} chars</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: 'var(--fg-secondary)' }}>Time to Expiry:</span>
                  <span
                    className="flex items-center gap-1"
                    style={{
                      color: (tokenInfo.timeToExpiry || 0) < 60000 ? 'var(--accent-warning)' : 'var(--fg-primary)'
                    } as any}
                  >
                    <Clock className="w-3 h-3" />
                    {Math.round((tokenInfo.timeToExpiry || 0) / 1000)}s
                  </span>
                </div>
              </>
            )}
          </div>
        </TestBlock>

        {/* 3. CONNECTION LIFECYCLE TEST */}
        <TestBlock
          title="3. Connection Lifecycle"
          description="Test WebSocket connection, disconnection, and reconnection"
          status={
            webSocketState.status === 'connected' ? 'success' :
            webSocketState.status === 'connecting' ? 'info' :
            webSocketState.status === 'auth_failed' ? 'error' :
            'idle'
          }
          actions={
            <div className="flex items-center gap-1">
              {webSocketState.status === 'disconnected' && (
                <button
                  onClick={testConnectionLifecycle}
                  disabled={!selectedExecutionId}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded"
                  style={{
                    backgroundColor: 'var(--accent)',
                    color: 'var(--bg-surface)'
                  }}
                  data-testid="dvws-connect"
                >
                  <Wifi className="w-3 h-3" />
                  Connect
                </button>
              )}

              {webSocketState.status === 'connected' && (
                <button
                  onClick={disconnectWebSocket}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded"
                  style={{
                    backgroundColor: 'var(--accent-warning)',
                    color: 'var(--bg-surface)'
                  } as any}
                  data-testid="dvws-disconnect"
                >
                  <Square className="w-3 h-3" />
                  Disconnect
                </button>
              )}

              {(['auth_failed', 'disconnected'].includes(webSocketState.status)) && (
                <button
                  onClick={reconnectWebSocket}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded"
                  style={{
                    backgroundColor: 'var(--bg-interactive-hover)',
                    color: 'var(--fg-primary)'
                  }}
                  data-testid="dvws-reconnect"
                >
                  <RotateCcw className="w-3 h-3" />
                  Retry
                </button>
              )}
            </div>
          }
        >
          <div className="space-y-3">
            {/* Connection Status Display */}
            <div data-testid="dvws-status">
              <ConnectionStatusIndicator
                status={webSocketState.status}
                isPollingFallback={webSocketState.isPollingFallback}
                error={webSocketState.error}
                lastUpdate={webSocketState.lastUpdate}
                onReconnect={reconnectWebSocket}
                compact={false}
              />
            </div>

            <div className="text-xs space-y-1" style={{ color: 'var(--fg-secondary)' }}>
              <div>Selected Execution: {selectedExecutionId || 'None'}</div>
              <div>Polling Fallback: {webSocketState.isPollingFallback ? 'Active' : 'Inactive'}</div>
              {webSocketState.lastUpdate && (
                <div>Last Update: {webSocketState.lastUpdate.toLocaleTimeString()}</div>
              )}
              {webSocketState.status === 'connected' && (
                <div style={{ color: 'var(--accent)' }}>
                  ✓ Ready to receive: pipeline_event, execution_update, stage_update
                </div>
              )}
            </div>
          </div>
        </TestBlock>

        {/* 4. EVENT STREAM TEST */}
        <TestBlock
          title="4. Event Stream & Cache"
          description="Live WebSocket events with cache invalidation"
          status={events.length > 0 ? 'success' : 'info'}
          actions={
            <div className="flex items-center gap-1">
              <button
                onClick={() => simulateWebSocketEvent('pipeline_event')}
                disabled={!selectedExecutionId}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: 'var(--bg-surface)'
                }}
              >
                Pipeline Event
              </button>
              <button
                onClick={() => simulateWebSocketEvent('stage_update')}
                disabled={!selectedExecutionId}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--bg-interactive-hover)',
                  color: 'var(--fg-primary)'
                }}
              >
                Stage Update
              </button>
            </div>
          }
          defaultOpen={true}
        >
          <EventFeed
            events={events}
            maxEvents={10}
            showPayloads={true}
            data-testid="websocket-demo-events"
          />
        </TestBlock>

        {/* 5. FAILURE SIMULATION DRILLS */}
        <TestBlock
          title="5. Failure Simulation Drills"
          description="Test WebSocket failure modes and recovery patterns"
          status={simulationInProgress ? 'info' : 'idle'}
          actions={
            <div className="flex items-center gap-1">
              <button
                onClick={simulateInvalidToken}
                disabled={!!simulationInProgress}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--accent-warning)',
                  color: 'var(--bg-surface)'
                } as any}
                data-testid="dvws-sim-invalid-token"
              >
                <AlertTriangle className="w-3 h-3" />
                Invalid Token
              </button>
              <button
                onClick={simulateTokenExpiry}
                disabled={!!simulationInProgress}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--accent-warning)',
                  color: 'var(--bg-surface)'
                } as any}
                data-testid="dvws-sim-token-expiry"
              >
                <Clock className="w-3 h-3" />
                Token Expiry
              </button>
            </div>
          }
          defaultOpen={true}
        >
          <div className="space-y-4">
            {/* Failure Simulation Controls */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={simulateServerDisconnect}
                disabled={!!simulationInProgress}
                className="flex items-center gap-2 px-3 py-2 text-sm rounded"
                style={{
                  backgroundColor: 'var(--bg-interactive-hover)',
                  color: 'var(--fg-primary)',
                  border: '1px solid var(--border-default)'
                }}
                data-testid="dvws-sim-disconnect"
              >
                <WifiOff className="w-4 h-4" />
                Server Disconnect
              </button>

              <button
                onClick={forceReauth}
                disabled={!!simulationInProgress}
                className="flex items-center gap-2 px-3 py-2 text-sm rounded"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: 'var(--bg-surface)'
                }}
                data-testid="dvws-force-reauth"
              >
                <Shield className="w-4 h-4" />
                Force Reauth
              </button>
            </div>

            {/* RLS/404 Simulation */}
            <div className="space-y-2">
              <label className="block text-xs" style={{ color: 'var(--fg-secondary)' }}>
                Test Execution ID (for RLS/404 simulation):
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={testExecutionId}
                  onChange={(e) => setTestExecutionId(e.target.value)}
                  placeholder="Enter execution ID or leave empty for random"
                  className="flex-1 p-2 text-sm rounded"
                  style={{
                    backgroundColor: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--fg-primary)'
                  }}
                />
                <button
                  onClick={simulateRlsDeny}
                  disabled={!!simulationInProgress}
                  className="flex items-center gap-1 px-3 py-2 text-sm rounded"
                  style={{
                    backgroundColor: 'var(--bg-interactive-hover)',
                    color: 'var(--fg-primary)',
                    border: '1px solid var(--border-default)'
                  }}
                >
                  <Zap className="w-4 h-4" />
                  Test RLS/404
                </button>
              </div>
            </div>

            {/* Polling Fallback Toggle */}
            <div className="flex items-center justify-between p-3 rounded" style={{
              backgroundColor: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)'
            }}>
              <div>
                <div className="text-sm font-medium" style={{ color: 'var(--fg-primary)' }}>
                  Polling Fallback
                </div>
                <div className="text-xs" style={{ color: 'var(--fg-secondary)' }}>
                  Enable automatic polling when WebSocket fails
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={enablePollingFallback}
                  onChange={(e) => setEnablePollingFallback(e.target.checked)}
                  className="sr-only peer"
                  data-testid="dvws-enable-fallback"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Current Simulation Status */}
            {simulationInProgress && (
              <div className="p-3 rounded" style={{
                backgroundColor: 'var(--bg-elevated)',
                border: '1px solid var(--accent)',
                borderColor: 'var(--accent)'
              }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--accent)' }}></div>
                  <span className="text-sm" style={{ color: 'var(--fg-primary)' }}>
                    Running simulation: {simulationInProgress.replace('_', ' ')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </TestBlock>

        {/* 6. FULL INTEGRATION TEST */}
        <TestBlock
          title="6. Full Integration"
          description="Complete end-to-end WebSocket flow test"
          status={
            webSocketState.status === 'connected' && executions.length > 0 && tokenInfo.hasToken
              ? 'success'
              : 'info'
          }
          actions={
            <button
              onClick={testFullIntegration}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded"
              style={{
                backgroundColor: 'var(--accent)',
                color: 'var(--bg-surface)'
              }}
            >
              <Play className="w-3 h-3" />
              Run Integration
            </button>
          }
        >
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>1. API Load:</span>
              <span style={{ color: executions.length > 0 ? 'var(--accent)' : 'var(--fg-secondary)' }}>
                {executions.length > 0 ? '✅' : '⏳'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>2. Token Auth:</span>
              <span style={{ color: tokenInfo.hasToken ? 'var(--accent)' : 'var(--fg-secondary)' }}>
                {tokenInfo.hasToken ? '✅' : '⏳'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>3. WebSocket:</span>
              <span style={{
                color: webSocketState.status === 'connected' ? 'var(--accent)' :
                       webSocketState.status === 'connecting' ? 'var(--accent-warning)' : 'var(--fg-secondary)'
              } as any}>
                {webSocketState.status === 'connected' ? '✅' :
                 webSocketState.status === 'connecting' ? '⏳' : '❌'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>4. Event Stream:</span>
              <span style={{ color: events.length > 0 ? 'var(--accent)' : 'var(--fg-secondary)' }}>
                {events.length > 0 ? `✅ (${events.length})` : '⏳'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>5. Cache Invalidation:</span>
              <span style={{ color: events.length > 0 ? 'var(--accent)' : 'var(--fg-secondary)' }}>
                {events.length > 0 ? '✅' : '⏳'}
              </span>
            </div>
          </div>
        </TestBlock>
      </div>
    </div>
  );
};